import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trophy } from "lucide-react";

export default function RankingTab() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [courseId, setCourseId] = useState("");
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.get("/admin/courses").then(({ data }) => setCourses(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setRows(null);
    const params = courseId ? { params: { course_id: courseId } } : {};
    api.get("/admin/ranking", params).then(({ data }) => setRows(data.rows)).catch(() => setRows([]));
  }, [courseId]);

  return (
    <Card className="institutional-card">
      <CardContent className="p-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
          <div className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-[#003366]" />
            <h3 className="font-display font-semibold text-slate-900">Graduatoria provvisoria</h3>
          </div>
          <select data-testid="ranking-course-filter"
            className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
            value={courseId} onChange={(e) => setCourseId(e.target.value)}>
            <option value="">Tutti i corsi</option>
            {courses.map((c) => <option key={c.id} value={c.id} data-testid={`ranking-course-option-${c.id}`}>{c.name}</option>)}
          </select>
        </div>
        <p className="text-xs text-slate-500 mb-5">
          Candidature completate ordinate per punteggio complessivo (test tecnico + colloquio). Chi non ha ancora
          sostenuto la valutazione di colloquio compare in coda, ordinato per punteggio del test. Vista di lavoro
          interna: non costituisce la graduatoria ufficiale.
        </p>

        {rows === null && <p className="text-sm text-slate-500">Caricamento graduatoria...</p>}
        {rows !== null && rows.length === 0 && (
          <p className="text-sm text-slate-500" data-testid="ranking-empty">Nessuna candidatura completata per il corso selezionato.</p>
        )}
        {rows !== null && rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ranking-table">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs text-slate-500">
                  <th className="py-2 pr-3 font-medium">#</th>
                  <th className="py-2 pr-3 font-medium">Candidato</th>
                  <th className="py-2 pr-3 font-medium">Corso</th>
                  <th className="py-2 pr-3 font-medium text-right">Test tecnico</th>
                  <th className="py-2 pr-3 font-medium text-right">Colloquio</th>
                  <th className="py-2 pr-3 font-medium text-right">Punteggio complessivo</th>
                  <th className="py-2 font-medium">Stato</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.application_id} data-testid={`ranking-row-${r.application_id}`}
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`/admin/candidature/${r.application_id}`)}>
                    <td className="py-2.5 pr-3 font-mono2 font-semibold text-[#003366]">{r.rank}</td>
                    <td className="py-2.5 pr-3">
                      <p className="font-medium text-slate-900">{r.name || "—"}</p>
                      <p className="text-xs text-slate-500">{r.email}</p>
                    </td>
                    <td className="py-2.5 pr-3 text-slate-600 text-xs">{r.course_name}</td>
                    <td className="py-2.5 pr-3 text-right font-mono2" data-testid={`ranking-test-a-${r.application_id}`}>
                      {r.test_a ?? "—"}{r.test_a_max ? <span className="text-slate-400">/{r.test_a_max}</span> : ""}
                    </td>
                    <td className="py-2.5 pr-3 text-right font-mono2">{r.interview_total ?? "—"}</td>
                    <td className="py-2.5 pr-3 text-right font-mono2 font-bold text-slate-900" data-testid={`ranking-total-${r.application_id}`}>
                      {r.interview_complete ? r.total : "—"}
                    </td>
                    <td className="py-2.5">
                      {r.interview_complete
                        ? <Badge className="bg-green-100 text-green-800">Completo</Badge>
                        : <Badge className="bg-amber-100 text-amber-800">In attesa del colloquio</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
